/**
 * Executive Report Page — editorial Guardian-voice template.
 *
 * Composition (top to bottom):
 *   1. Sub-header: period selector + Download PDF button. Page title is
 *      carried by the breadcrumb; we deliberately don't render an h1 here
 *      (the standfirst owns that role).
 *   2. ExecutiveStandfirst — kicker, italic-mixed headline, deck, byline,
 *      dark-navy ScoreBox with the four-component composite. Reuses ScoreBox
 *      from Dashboard v2 verbatim.
 *   3. Operational KpiStrip — three-up: drift remediated, violations resolved,
 *      open critical. Reuses PostureBrief KpiStrip verbatim. The other three
 *      metrics from `report.metrics` (waivers, total drift, open high) are
 *      surfaced in a tracked-uppercase secondary line so we don't lose them.
 *   4. ExecutiveTrendChart — guardian-score trend reframed with editorial
 *      chrome (eyebrow + italic headline).
 *   5. FrameworkRoll — replaces the dense table with severity-ordered
 *      named-subject rows (italic dropcap score + delta + control coverage).
 *   6. NarrativeBlock — AI executive narrative as a pull-quote-style article
 *      with kicker + deterministic deck + byline strip.
 *   7. Footer rule — tracked-uppercase byline strip mirroring the standfirst
 *      bracket; we don't use FooterRail because the Executive Report isn't
 *      a pillar (no cross-pillar jump links to surface).
 *
 * Loading state: while `useExecutiveReport.isLoading` is true we render the
 * BuildingLoader (multi-stage Guardian-voice walkthrough) in place of the
 * standfirst + body. Backend generation is synchronous (~10-30s); the loader
 * keeps the user oriented without faking a real progress feed.
 *
 * Hard-preserved (regression surface): the Download PDF action and its
 * `api.downloadExecutiveReport` call, the period selector (30/60/90 days),
 * tenant context auto-sync from the sidebar.
 */

import { useEffect, useMemo, useState } from 'react'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { useTenantContext } from '@/hooks/useTenantContext'
import { useExecutiveReport } from '@/hooks/useExecutiveReport'
import { api } from '@/lib/api'
import {
  composeExecutiveReportBrief,
  executiveReportToBriefInput,
} from '@/lib/executive-report-brief'
import type { ExecutiveReportData } from '@/types/executiveReport'

import ExecutiveStandfirst from '@/components/ExecutiveReport/ExecutiveStandfirst'
import BuildingLoader from '@/components/ExecutiveReport/BuildingLoader'
import FrameworkRoll from '@/components/ExecutiveReport/FrameworkRoll'
import ExecutiveTrendChart from '@/components/ExecutiveReport/ExecutiveTrendChart'
import NarrativeBlock from '@/components/ExecutiveReport/NarrativeBlock'
import KpiStrip, { type KpiCell } from '@/components/PostureBrief/KpiStrip'

const PERIOD_OPTIONS = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
] as const

export default function ExecutiveReport() {
  const { defaultTenantId, userTenants, isLoading: contextLoading } = useTenantContext()
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined)
  const [periodDays, setPeriodDays] = useState<number>(30)
  const [isDownloading, setIsDownloading] = useState(false)

  // Sync selected tenant from context on first load.
  useEffect(() => {
    if (!contextLoading && defaultTenantId) {
      setSelectedTenantId(defaultTenantId)
    }
  }, [contextLoading, defaultTenantId])

  const { data: report, isLoading, error } = useExecutiveReport(selectedTenantId, periodDays)

  const tenantName = useMemo(() => {
    if (!selectedTenantId || !userTenants) return ''
    // userTenants is intentionally typed as any[] in the context — the
    // narrowing here matches the legacy lookup pattern.
    return (
      (userTenants as { tenantId: string; name?: string }[]).find(
        t => t.tenantId === selectedTenantId,
      )?.name ?? ''
    )
  }, [selectedTenantId, userTenants])

  const handleDownload = async () => {
    if (!selectedTenantId) return
    setIsDownloading(true)
    try {
      const blob = await api.downloadExecutiveReport(selectedTenantId, 'pdf', periodDays)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `executive-report-${tenantName || 'tenant'}-${new Date().toISOString().split('T')[0]}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // We surface failures via the existing toast layer elsewhere; logging
      // the error here keeps the diagnostic without crashing the page.
      console.error('Failed to download report:', err)
    } finally {
      setIsDownloading(false)
    }
  }

  /* --------------- Render guards --------------- */

  if (contextLoading) {
    return (
      <div className="mx-auto max-w-[1320px]">
        <BuildingLoader periodDays={periodDays} />
      </div>
    )
  }

  if (!selectedTenantId) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p
          className="text-[14px] italic"
          style={{ color: 'rgb(var(--color-text-secondary))' }}
        >
          Select a tenant from the sidebar to compose an executive report.
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-[14px]" style={{ color: '#C84B4B' }}>
          Failed to load report data. Please try again.
        </p>
      </div>
    )
  }

  /* --------------- Brief composition --------------- */

  // The brief is computed off whatever data we have — null/undefined slot
  // gracefully into the loading branch. We still call the composer in the
  // loading state so the standfirst we *would* render (e.g. on a stale
  // re-fetch) stays consistent across mounts.
  const brief = composeExecutiveReportBrief(
    executiveReportToBriefInput(report ?? null, {
      tenantId: selectedTenantId,
      tenantName,
      periodDays,
    }),
  )

  /* --------------- Render --------------- */

  return (
    <div className="mx-auto max-w-[1320px]">
      {/* Sub-header: period selector + download. The breadcrumb carries the
          page title so we don't render an h1 here. */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <p
          className="text-[12px] font-bold uppercase tracking-[0.18em]"
          style={{ color: 'rgb(var(--color-text-secondary))' }}
        >
          {tenantName ? `${tenantName} · Executive view` : 'Executive view'}
        </p>
        <div className="flex items-center gap-3">
          <select
            value={periodDays}
            onChange={e => setPeriodDays(Number(e.target.value))}
            className="input-dark py-2 text-sm"
            aria-label="Reporting period"
          >
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            onClick={handleDownload}
            disabled={isLoading || isDownloading}
            className="btn-primary inline-flex items-center disabled:opacity-50"
            aria-label="Download executive report as PDF"
          >
            <ArrowDownTrayIcon className="mr-2 h-4 w-4" />
            {isDownloading ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <BuildingLoader periodDays={periodDays} />
      ) : report ? (
        <>
          <ExecutiveStandfirst
            brief={brief}
            score={report.currentGuardianScore}
            components={buildCompositeBars(report)}
          />

          <KpiStrip tone="compliance" cells={buildKpiCells(report, periodDays)} />

          {/* Secondary metrics line — surfaces the three KPIs we couldn't
              fit in the three-up strip without re-introducing naked tiles. */}
          <SecondaryMetricsLine report={report} />

          <ExecutiveTrendChart data={report.guardianScoreTrend} periodDays={periodDays} />

          <section className="mt-12">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
              Section · 03 · Frameworks
            </div>
            <h2
              className="mt-1 font-display font-extrabold tracking-[-0.018em] text-[28px]"
              style={{ color: 'rgb(var(--color-text-primary))' }}
            >
              Where the frameworks{' '}
              <em
                style={{
                  fontStyle: 'italic',
                  fontWeight: 600,
                  color: 'rgb(var(--color-primary-700))',
                }}
              >
                stand.
              </em>
            </h2>
            <p
              className="mt-2 max-w-2xl text-[14px] italic"
              style={{ color: 'rgb(var(--color-text-secondary))' }}
            >
              Severity-ordered, weakest first. Frameworks marked “In score”
              fold into the composite Guardian Score; the rest count toward
              audit readiness.
            </p>

            <div className="mt-6">
              <FrameworkRoll frameworks={report.frameworkScores} />
            </div>
          </section>

          <NarrativeBlock
            narrative={report.executiveNarrative}
            deck={brief.narrativeDeck}
            tenantName={tenantName || 'your tenant'}
            periodDays={periodDays}
          />

          {/* Closing rule — mirrors the standfirst-rule at the page top.
              Carries the last-refreshed timestamp so the executive can see
              the data freshness without hunting for it. */}
          <footer
            className="
              mt-14 flex flex-wrap items-center justify-between gap-5 pt-7
              text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500
            "
            style={{
              borderTopStyle: 'double',
              borderTopWidth: '4px',
              borderTopColor: '#212938',
            }}
          >
            <span>End of brief</span>
            <span>
              Generated{' '}
              <time dateTime={report.generatedAt}>
                {formatTimestamp(report.generatedAt)}
              </time>
            </span>
          </footer>
        </>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Build the four-bar composite for the ScoreBox panel. Mirrors the same
 * weighting + colors the Command Center uses on its standfirst (so the same
 * tenant sees a visually identical breakdown across surfaces).
 */
function buildCompositeBars(report: ExecutiveReportData) {
  return [
    {
      label: 'Baseline drift',
      weight: `${report.baselineCompliance.weight}%`,
      value: Math.round(report.baselineCompliance.current ?? 0),
      fillClass: 'bg-primary-500',
    },
    {
      label: 'Best practice',
      weight: `${report.bpaScore.weight}%`,
      value: Math.round(report.bpaScore.current ?? 0),
      fillClass: 'bg-secondaryBlue-300',
    },
    {
      label: 'MFA adoption',
      weight: `${report.mfaAdoption.weight}%`,
      value: Math.round(report.mfaAdoption.current ?? 0),
      fillClass: 'bg-secondarySand-300',
    },
    {
      label: 'M365 Secure Score',
      weight: `${report.secureScore.weight}%`,
      value: Math.round(report.secureScore.current ?? 0),
      fillClass: 'bg-primary-500',
    },
  ]
}

/**
 * Build the three editorially-meaningful KPI cells. We picked drift remediated,
 * violations resolved, and open critical because they answer the three
 * questions an executive scans for: what got fixed, what got cleared, and
 * what's still on fire. The other three metrics surface in the secondary
 * line below the strip.
 */
function buildKpiCells(report: ExecutiveReportData, periodDays: number): KpiCell[] {
  const m = report.metrics
  const pad = (n: number): string => String(n).padStart(2, '0')

  // Open-critical severity tint: anything > 0 should pull the eye, > 5
  // escalates to the red dropcap. Same band thresholds the posture pages use.
  const criticalSeverity =
    m.openCriticalViolations === 0
      ? 'tone'
      : m.openCriticalViolations > 5
        ? 'crit'
        : 'warn'

  return [
    {
      id: 'drift-remediated',
      numeral: pad(m.driftItemsRemediated),
      label: 'Drift remediated',
      stat: pluralizeItems(m.driftItemsRemediated, 'configuration drift closed', 'configuration drifts closed'),
      trend: `Across the **${periodDays}-day** window`,
      severity: 'tone',
    },
    {
      id: 'violations-resolved',
      numeral: pad(m.violationsResolved),
      label: 'Violations resolved',
      stat: pluralizeItems(m.violationsResolved, 'compliance gap closed', 'compliance gaps closed'),
      trend: `Across the **${periodDays}-day** window`,
      severity: 'tone',
    },
    {
      id: 'open-critical',
      numeral: pad(m.openCriticalViolations),
      label: 'Open critical',
      stat:
        m.openCriticalViolations === 0
          ? 'Nothing critical outstanding'
          : pluralizeItems(
              m.openCriticalViolations,
              'critical item still open',
              'critical items still open',
            ),
      trend:
        m.openCriticalViolations === 0
          ? 'The critical lane is clear'
          : `Worth eyes on this sprint`,
      severity: criticalSeverity,
    },
  ]
}

function pluralizeItems(n: number, singular: string, plural: string): string {
  if (n === 0) return `0 ${plural}`
  if (n === 1) return `1 ${singular}`
  return `${n} ${plural}`
}

/** Tracked-uppercase secondary line — surfaces the three metrics we couldn't
 *  fit in the three-up strip without re-introducing naked-tile fatigue. */
function SecondaryMetricsLine({ report }: { report: ExecutiveReportData }) {
  const m = report.metrics
  const items: { label: string; value: string }[] = [
    { label: 'Drift detected', value: String(m.totalDriftDetected) },
    { label: 'Waivers granted', value: String(m.waiversGranted) },
    { label: 'Open high', value: String(m.openHighViolations) },
  ]
  return (
    <div
      className="
        -mt-3 mb-4 flex flex-wrap gap-x-7 gap-y-2 px-7
        text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500
      "
    >
      {items.map((it, i) => (
        <span key={it.label} className="flex items-center gap-2">
          <span>{it.label}</span>
          <span
            className="font-mono text-[12px] tabular-nums"
            style={{ color: 'rgb(var(--color-text-primary))' }}
          >
            {it.value}
          </span>
          {i < items.length - 1 && <span aria-hidden className="ml-3 opacity-40">·</span>}
        </span>
      ))}
    </div>
  )
}

/** Render an ISO timestamp as a short, locale-aware string. The byline
 *  surface uppercases everything so we don't need to be precious about
 *  the rendered case here. */
function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}
